import React from 'react';

/**
 * Classic left-title / right-body split for long page sections.
 * items-start keeps the title from floating above a tall empty column.
 */
export function SplitSection({ eyebrow, title, children, className = '' }) {
  return (
    <div className={`grid lg:grid-cols-12 gap-x-10 xl:gap-x-16 gap-y-6 items-start ${className}`}>
      <div className="lg:col-span-5">
        {eyebrow ? (
          <div className="text-[#ff8ec5] text-xs tracking-[0.3em] uppercase mb-4">{eyebrow}</div>
        ) : null}
        <h2 className="font-semibold tracking-tight text-white text-2xl sm:text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.08]">
          {title}
        </h2>
      </div>
      <div className="lg:col-span-7 space-y-5 text-white/60 text-base sm:text-lg leading-relaxed">
        {children}
      </div>
    </div>
  );
}

/**
 * Framer / Isoline-style editorial intro:
 * eyebrow → oversized title → hairline → lead → supporting copy in a 2-col grid.
 */

export function EditorialIntro({
  eyebrow,
  title,
  lead,
  children,
  className = '',
  logo,
}) {
  const support = React.Children.toArray(children).filter(Boolean);

  return (
    <div className={className}>
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 xl:gap-20 items-start">

        {/* LEFT COLUMN */}
        <div className="lg:col-span-6 xl:col-span-6">

          {/* Logo + Vertical Magenta Accent Line */}
          {logo ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3.5 sm:gap-4 mb-6 sm:mb-8">
              {/* Vertical magenta accent: on mobile stacks after logo; on desktop sits to the left of the logo */}
              <div className="order-2 sm:order-1 w-[3px] sm:w-[3.5px] h-7 sm:h-9 bg-[#d42b92] rounded-full shrink-0" />
              <div className="order-1 sm:order-2 flex items-center">
                {typeof logo === 'string' ? (
                  logo.includes('issi') ? (
                    <img
                      src={logo}
                      alt="iSSi"
                      className="h-11 sm:h-12 md:h-14 w-auto object-contain"
                    />
                  ) : logo.includes('studio') ? (
                    <div className="h-9 sm:h-10 px-3.5 py-1.5 rounded-2xl bg-black border border-white/20 flex items-center justify-center shadow-sm">
                      <img
                        src={logo}
                        alt={eyebrow || 'Brand'}
                        className="h-5 sm:h-5.5 w-auto max-w-[130px] object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.nextElementSibling;
                          if (fallback) fallback.style.display = 'inline-block';
                        }}
                      />
                      <span className="hidden text-xs font-semibold text-white/90 tracking-tight">
                        {eyebrow || 'Studio-I'}
                      </span>
                    </div>
                  ) : (
                    <div className="h-9 sm:h-10 px-3.5 py-1.5 rounded-2xl bg-white/95 border border-white/20 flex items-center justify-center shadow-sm">
                      <img
                        src={logo}
                        alt={eyebrow || 'Brand'}
                        className="h-5 sm:h-5.5 w-auto max-w-[130px] object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.nextElementSibling;
                          if (fallback) fallback.style.display = 'inline-block';
                        }}
                      />
                      <span className="hidden text-xs font-semibold text-black/80 tracking-tight">
                        {eyebrow || 'InnovHer'}
                      </span>
                    </div>
                  )
                ) : (
                  logo
                )}
              </div>
            </div>
          ) : eyebrow ? (
            <div className="text-[#ff8ec5] text-[11px] font-medium tracking-[0.28em] uppercase mb-5">
              {eyebrow}
            </div>
          ) : null}

          {/* Headline */}
          <div className="max-w-xl xl:max-w-2xl">
            <h2 className="font-semibold tracking-[-0.035em] text-white text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] xl:text-[3.5rem] leading-[1.05] sm:leading-[1.04] lg:leading-[1.02]">
              {title}
            </h2>
          </div>

          {/* Lead */}
          {lead ? (
            <p className="mt-6 sm:mt-7 lg:mt-8 max-w-xl text-white/80 text-lg sm:text-xl lg:text-[1.25rem] xl:text-[1.32rem] leading-[1.48] sm:leading-[1.45] font-normal italic">
              {lead}
            </p>
          ) : null}
        </div>

        {/* RIGHT COLUMN */}
        <div className="lg:col-span-6 pt-0 lg:pt-14 xl:pt-16 xl:col-span-6">
          <div className="max-w-[580px] space-y-6 sm:space-y-7 pt-1 lg:pt-2">
            {support.map((child, i) => (
              <div
                key={i}
                className="text-white/60 text-[15px] sm:text-base lg:text-[1.02rem] leading-[1.62] sm:leading-[1.65]"
              >
                {child}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

export function IntroSplit({ children, className = '' }) {
  const items = React.Children.toArray(children);
  return (
    <div className={`grid sm:grid-cols-2 gap-5 sm:gap-6 lg:gap-8 ${className}`}>
      {items.map((child, i) => (
        <div key={i} className="text-white/60 text-base sm:text-lg leading-relaxed">
          {child}
        </div>
      ))}
    </div>
  );
}
